<template>
  <div>
    <!-- Inspirational header for target audience -->
    <div class="text-center mb-10">
      <p class="text-lg text-purple-700 font-medium italic mb-2">
        Откройте путь к гармонии и внутреннему преображению
      </p>
      <p class="text-gray-600 max-w-2xl mx-auto">
        Каждый набор мандал — это ваш персональный инструмент для глубокой работы с намерениями через медитативное рисование
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch mb-12">
      <div v-for="(pkg, index) in packages" :key="pkg.id"
          :class="[
            'relative bg-white rounded-2xl shadow-xl h-full flex flex-col border',
            {
              'border-4 border-purple-500 transform md:scale-105': index === 1,
              'border-gray-200': index !== 1
            }
          ]">
            <div v-if="index === 1" class="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span class="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-1.5 rounded-full text-sm font-semibold shadow whitespace-nowrap">
                Лучший выбор
              </span>
            </div>
            <div v-else-if="index === 2" class="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span class="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-1.5 rounded-full text-sm font-semibold shadow whitespace-nowrap">
                Максимум возможностей
              </span>
            </div>

            <div class="p-8 flex-grow pt-10">
              <h3 class="text-2xl font-semibold mb-3 text-gray-900">{{ getPackageName(index) }}</h3>
              <p class="mb-4 text-purple-600 font-medium">{{ getPackageTagline(index) }}</p>

              <!-- Package highlight -->
              <div class="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
                <div class="text-center">
                  <span class="block text-3xl font-bold text-purple-700">{{ getIntentionsCount(index) }}</span>
                  <span class="text-sm text-purple-600">{{ getIntentionsLabel(index) }}</span>
                </div>
                <p class="text-xs text-gray-600 text-center mt-2">
                  На каждое намерение — полный набор из 11-15 шаблонов мандал
                </p>
              </div>

              <!-- What's included -->
              <div class="mb-4">
                <h4 class="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <span class="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2"></span>
                  Состав каждого набора:
                </h4>
                <ul class="space-y-2 text-sm text-gray-600">
                  <li class="flex items-start">
                    <CheckIcon class="h-4 w-4 text-purple-500 mr-2 flex-shrink-0 mt-0.5"/>
                    <span><strong>Цветная мандала</strong> — для созерцания и медитации</span>
                  </li>
                  <li class="flex items-start">
                    <CheckIcon class="h-4 w-4 text-purple-500 mr-2 flex-shrink-0 mt-0.5"/>
                    <span><strong>Цифровая мандала</strong> — для понимания энергии чисел</span>
                  </li>
                  <li class="flex items-start">
                    <CheckIcon class="h-4 w-4 text-purple-500 mr-2 flex-shrink-0 mt-0.5"/>
                    <span><strong>9-13 точечных мандал</strong> — для раскрашивания и проработки</span>
                  </li>
                </ul>
              </div>

              <!-- Methods included -->
              <div class="mb-4">
                <h4 class="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <span class="w-1.5 h-1.5 bg-pink-500 rounded-full mr-2"></span>
                  Методы расчёта (на 9, 12, 13):
                </h4>
                <p class="text-xs text-gray-500 mb-2">Базовые методы:</p>
                <div class="flex flex-wrap gap-1.5 text-xs mb-2">
                  <span class="bg-purple-100 text-purple-700 px-2 py-1 rounded">Метод 1аб</span>
                  <span class="bg-purple-100 text-purple-700 px-2 py-1 rounded">Метод 2аб</span>
                  <span class="bg-purple-100 text-purple-700 px-2 py-1 rounded">Метод 5аб</span>
                  <span class="bg-purple-100 text-purple-700 px-2 py-1 rounded">Метод 6</span>
                  <span class="bg-purple-100 text-purple-700 px-2 py-1 rounded">Квадрат</span>
                </div>
                <p class="text-xs text-gray-500 mb-2">Продвинутые методы:</p>
                <div class="flex flex-wrap gap-1.5 text-xs">
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Метод 3</span>
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Метод 4</span>
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Метод 8аб</span>
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Шестиугольник</span>
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Шестиугольник подлинный</span>
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Квадрат 2</span>
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Квадрат 5аб</span>
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Магический квадрат</span>
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Звезда гексагон</span>
                  <span class="bg-pink-100 text-pink-700 px-2 py-1 rounded">Кормовая форма</span>
                </div>
              </div>

              <!-- Templates included -->
              <div class="mb-4">
                <h4 class="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                  <span class="w-1.5 h-1.5 bg-amber-500 rounded-full mr-2"></span>
                  Формы шаблонов:
                </h4>
                <ul class="space-y-1.5 text-xs text-gray-600">
                  <li class="flex items-start">
                    <span class="text-amber-500 mr-2 mt-0.5">&#9670;</span>
                    <span><strong>Шестиугольник</strong> — шаблон из ромбов, 6 секторов</span>
                  </li>
                  <li class="flex items-start">
                    <span class="text-amber-500 mr-2 mt-0.5">&#9670;</span>
                    <span><strong>Восьмиугольник</strong> — шаблон из ромбов, 8 секторов</span>
                  </li>
                  <li class="flex items-start">
                    <span class="text-amber-500 mr-2 mt-0.5">&#9670;</span>
                    <span><strong>Соты</strong> — шаблон из правильных шестиугольников</span>
                  </li>
                  <li class="flex items-start">
                    <span class="text-amber-500 mr-2 mt-0.5">&#9670;</span>
                    <span><strong>Квадрат</strong> — классическая квадратная форма</span>
                  </li>
                  <li class="flex items-start">
                    <span class="text-amber-500 mr-2 mt-0.5">&#9670;</span>
                    <span><strong>Звезда гексагон</strong> — шестилучевая звезда</span>
                  </li>
                  <li class="flex items-start">
                    <span class="text-amber-500 mr-2 mt-0.5">&#9670;</span>
                    <span><strong>Кормовая форма</strong> — 6 лучей</span>
                  </li>
                </ul>
              </div>

              <!-- Bonus -->
              <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                <p class="text-sm text-amber-800 flex items-start">
                  <span class="text-amber-500 mr-2 text-lg leading-none">&#10022;</span>
                  <span><strong>Бонус:</strong> Печатная форма в формате PDF — сразу готова к распечатке</span>
                </p>
              </div>
            </div>

            <div class="p-8 pt-0 mt-auto">
              <div class="text-3xl font-bold mb-1" :class="index === 1 ? 'text-purple-600' : 'text-gray-900'">
                {{ formatPrice(pkg.price) }} ₽
              </div>
              <div class="text-sm mb-4 text-gray-500">
                Единоразовый платёж • Доступ навсегда
              </div>
              <button @click="handlePurchase(pkg)"
                      :class="[
                        'w-full px-6 py-3 rounded-lg font-semibold transition duration-150 focus:outline-none focus:ring-offset-2',
                        index === 1
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 focus:ring-2 focus:ring-purple-500'
                          : 'bg-purple-600 text-white hover:bg-purple-700 focus:ring-2 focus:ring-purple-500'
                      ]">
                {{ index === 1 ? 'Выбрать лучший тариф' : 'Начать преображение' }}
              </button>
              <p class="text-xs text-gray-500 text-center mt-3">
                Мгновенный доступ после оплаты
              </p>
            </div>
        </div>
    </div>

    <!-- Trust elements for target audience -->
    <div class="text-center mt-8 p-6 bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 rounded-2xl">
      <p class="text-purple-700 font-medium mb-2">
        Более 1000 женщин уже используют мандалы для внутренней трансформации
      </p>
      <p class="text-sm text-gray-600">
        Безопасная оплата • Поддержка на каждом этапе • Подробные инструкции
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { CheckIcon } from '@heroicons/vue/24/outline'
import axiosInstance from '@/axios/axios'
import { useRouter } from 'vue-router'

const props = defineProps({
  packages: {
    type: Array,
    required: true
  }
})

const router = useRouter()

// Package names for the three tiers
function getPackageName(index) {
  const names = [
    'Один набор мандал',
    'Пять наборов мандал',
    'Десять наборов мандал'
  ]
  return names[index] || `Набор ${index + 1}`
}

// Marketing taglines for target audience
function getPackageTagline(index) {
  const taglines = [
    'Идеально для первого знакомства с методикой',
    'Для глубокой проработки нескольких сфер жизни',
    'Полная трансформация во всех направлениях'
  ]
  return taglines[index] || ''
}

// Number of intentions per package
function getIntentionsCount(index) {
  const counts = ['1', '5', '10']
  return counts[index] || '1'
}

// Label for intentions
function getIntentionsLabel(index) {
  const labels = [
    'намерение (фраза)',
    'намерений (фраз)',
    'намерений (фраз)'
  ]
  return labels[index] || 'намерение'
}

function formatPrice(price) {
  return Number(price).toLocaleString('ru-RU')
}

async function handlePurchase(pkg) {
    if (!pkg) return;

    try {
        const response = await axiosInstance.post(`/draw/api/v1/free/packages/purchase/${pkg.id}/`);
        if (response.data && response.data.payment_link) {
            window.location.href = response.data.payment_link;
        }
    } catch (error) {
        console.error("Purchase error:", error);
        // Handle error (e.g., show toast)
        if (error.response && error.response.status === 401) {
             // Redirect to login with redirect to payment page
             router.push({
               name: 'SignIn',
               query: { redirect: '/payment' }
             })
        }
    }
}
</script>
